import React, { useEffect, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import Switch from '@mui/material/Switch';
import { localurl } from '../utils';
import { Toaster } from 'react-hot-toast';
import toast from 'react-hot-toast';
import axios from 'axios';
import { MdOutlineArrowBackIosNew } from "react-icons/md";
import { IoEyeOutline } from "react-icons/io5";
import { IoEyeOffOutline } from "react-icons/io5";
const AddAgentForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;
    const [showPassword, setShowPassword] = useState(false);

    const [initialValues, setInitialValues] = useState({
        username: '',
        email: '',
        password: '',
        is_active: true,
        availability: {
            is_24_7: false,
            working_days: [],
            start_time: '',
            end_time: '',
        },
    });

    const getValidationSchema = (isEditMode) =>
        Yup.object({
            username: Yup.string().required('Name is required').matches(/^[A-Za-z0-9]+$/, 'No spaces, commas, or dashes allowed'),
            email: Yup.string()
                .email('Invalid email')
                .required('Email is required'),
            password: Yup.string().when([], {
                is: () => !isEditMode,
                then: (schema) => schema.required('Password is required'),
                otherwise: (schema) => schema,
            }),
        });
    useEffect(() => {
        const fetchAgentData = async () => {
            if (!isEditMode) return;

            try {
                const response = await axios.get(`${localurl}/agents/${id}/`, {
                    headers: {
                    },
                });

                const agentData = {
                    ...response.data,
                    password: '',
                    availability: {
                        ...response.data.availability,
                        start_time: response.data.availability?.start_time?.substring(0, 5) || '',
                        end_time: response.data.availability?.end_time?.substring(0, 5) || ''
                    }
                };

                setInitialValues(agentData);
            } catch (error) {
                toast.error('Failed to fetch agent data');
            }
        };

        fetchAgentData();
    }, [id, isEditMode]);

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            const payload = { ...values };

            if (isEditMode) {
                if (!payload.password) delete payload.password;

                // ✅ Don't send email again during editing
                delete payload.email;
            }

            const url = isEditMode
                ? `${localurl}/agents/${id}/`
                : `${localurl}/create/agent/`;

            await axios({
                method: isEditMode ? 'PUT' : 'POST',
                url,
                headers: {
                },
                data: payload
            });

            toast.success(`Agent ${isEditMode ? 'updated' : 'created'} successfully!`);
            setTimeout(() => {
                navigate('/agents');
            }, 500);
        } catch (error) {
            const errorMessage = error.response?.data?.email
                ? 'Email already exists!'
                : error.response?.data?.message || `Error ${isEditMode ? 'updating' : 'creating'} agent`;
            toast.error(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };


    return (
        <div className="">
            <button onClick={() => navigate(-1)} className="mb-4 text-black bg-white rounded-lg p-4">
                <MdOutlineArrowBackIosNew />
            </button>
            <Formik
                initialValues={initialValues}
                validationSchema={getValidationSchema(isEditMode)}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ values, setFieldValue, isSubmitting }) => (
                    <Form className="space-y-6">
                        <div className='p-3 bg-white rounded-xl'>
                            <p className='font-semibold text-xl text-[#3F3F46]'>{isEditMode ? 'Edit Agent' : 'Add New Agent'}</p>
                            <p className='font-normal text-sm text-[#71717A]'>{isEditMode ? 'Edit Existing Agent' : 'Create a new support agent'}</p>
                        </div>
                        <div className="bg-white p-6 rounded-xl shadow-sm">
                            <h3 className="text-lg font-semibold mb-6">Agent Information</h3>

                            <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        User Name
                                    </label>
                                    <Field
                                        name="username"
                                        className="w-full p-2 border rounded-lg"
                                        placeholder="JohnDoe"
                                    />
                                    <ErrorMessage
                                        name="username"
                                        component="div"
                                        className="text-red-500 text-sm mt-1"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        Email
                                    </label>
                                    <Field
                                        name="email"
                                        type="email"
                                        className="w-full p-2 border rounded-lg"
                                        placeholder="agent@example.com"
                                        readOnly={isEditMode}
                                    />
                                    <ErrorMessage
                                        name="email"
                                        component="div"
                                        className="text-red-500 text-sm mt-1"
                                    />
                                </div>
                                <div>
                                    <div className=' flex flex-col'>
                                        <label className='block text-sm font-medium mb-2'>Password</label>
                                    </div>
                                    <div className='relative'>
                                        <Field type={showPassword ? "text" : "password"} name="password" placeholder={isEditMode ? 'Enter New password' : 'Password'} className="w-full p-2 border rounded-lg" />
                                        <span className='absolute z-30 cursor-pointer right-4 top-3' onClick={() => setShowPassword(!showPassword)}>
                                            {showPassword ? (<IoEyeOutline className=' size-5' />) : (<IoEyeOffOutline className=' size-5' />)}
                                        </span>
                                    </div>
                                    <ErrorMessage name="password" component="div" style={{ color: 'red' }} />
                                </div>
                            </div>
                        </div>

                        {/* Availability Section */}
                        <div className="bg-white p-6 rounded-xl shadow-sm">
                            <h3 className="text-lg font-semibold mb-6">Working Schedule</h3>

                            <div className="flex items-center justify-between p-4 border rounded-lg mb-6">
                                <div>
                                    <p className="font-medium">24/7 Availability</p>
                                    <p className="text-sm text-gray-500">
                                        Agent available around the clock?
                                    </p>
                                </div>
                                <Switch
                                    checked={values.availability.is_24_7}
                                    onChange={(e) => setFieldValue('availability.is_24_7', e.target.checked)}
                                />
                            </div>

                            {!values.availability.is_24_7 && (
                                <>
                                    <div className="mb-10">
                                        <label className="block text-sm font-medium mb-4">
                                            Working Days
                                        </label>
                                        <div className="flex flex-row md:flex-nowrap flex-wrap gap-4">
                                            {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                                                <label key={day} className="flex items-center space-x-2">
                                                    <Field
                                                        type="checkbox"
                                                        name="availability.working_days"
                                                        value={day}
                                                        className="form-checkbox h-4 w-4"
                                                    />
                                                    <span>{day}</span>
                                                </label>
                                            ))}
                                        </div>
                                        <ErrorMessage
                                            name="availability.working_days"
                                            component="div"
                                            className="text-red-500 text-sm mt-2"
                                        />
                                    </div>

                                    <div className="grid md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium mb-2">
                                                Start Time
                                            </label>
                                            <Field
                                                type="time"
                                                name="availability.start_time"
                                                className="w-full p-2 border rounded-lg"
                                            />
                                            <ErrorMessage
                                                name="availability.start_time"
                                                component="div"
                                                className="text-red-500 text-sm mt-1"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium mb-2">
                                                End Time
                                            </label>
                                            <Field
                                                type="time"
                                                name="availability.end_time"
                                                className="w-full p-2 border rounded-lg"
                                            />
                                            <ErrorMessage
                                                name="availability.end_time"
                                                component="div"
                                                className="text-red-500 text-sm mt-1"
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Status Section */}
                        <div className="bg-white p-6 rounded-xl shadow-sm">
                            <div className="flex items-center justify-between p-4 border rounded-lg">
                                <div>
                                    <p className="font-medium">Active Status</p>
                                    <p className="text-sm text-gray-500">
                                        Enable/disable this agent account
                                    </p>
                                </div>
                                <Switch
                                    checked={values.is_active}
                                    onChange={(e) => setFieldValue('is_active', e.target.checked)}
                                />
                            </div>
                        </div>

                        <div className="flex justify-end">
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="px-6 py-2 text-white bg-[#c5003e] rounded-lg hover:bg-[#a40031] disabled:bg-gray-400"
                            >
                                {isSubmitting
                                    ? (isEditMode ? 'Updating...' : 'Creating...')
                                    : (isEditMode ? 'Update Agent' : 'Create Agent')}
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
            <Toaster />
        </div>
    );
};

export default AddAgentForm;
